// Reusable progress UI for lab / scan requests.
//
// One row in lab_request has a `progress_status` of:
//   paid → sample_taken → processing → done
//
// This file exports two small pieces the worklist pages compose:
//
//   <ProgressChip status={...} />
//      Compact pill rendering of the current stage. Drop into list rows.
//
//   <AdvanceProgressButton reqNo={...} status={...} onAdvanced={fn} />
//      Single-click action that moves the request forward to the next
//      stage. Forward-only; the button hides itself when nothing further
//      can be advanced (i.e. status is already "done").
//
// Both use the same STAGES table so labels, colours and ordering stay in
// lock-step. Adding a new stage = one entry in STAGES.

import React, { useState } from 'react';
import { Box, Chip, Button, Tooltip, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Science as SampleIcon,
  AutoMode as ProcessingIcon,
  Verified as DoneIcon,
  TaskAlt as PaidIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { advanceRequestProgressAPI } from '../BaseURL';

export const STAGES = [
  { key: 'paid',         label: 'Paid',         short: 'Paid',         icon: <PaidIcon       sx={{ fontSize: '14px !important' }} />, color: '#0ea5e9', next: 'sample_taken' },
  { key: 'sample_taken', label: 'Sample Taken', short: 'Sample',       icon: <SampleIcon     sx={{ fontSize: '14px !important' }} />, color: '#7c3aed', next: 'processing'   },
  { key: 'processing',   label: 'Processing',   short: 'Processing',   icon: <ProcessingIcon sx={{ fontSize: '14px !important' }} />, color: '#f59e0b', next: 'done'         },
  { key: 'done',         label: 'Done',         short: 'Done',         icon: <DoneIcon       sx={{ fontSize: '14px !important' }} />, color: '#15803d', next: null           },
];

// Lookup helper that tolerates unknown / legacy values by falling back to
// "paid" — the initial stage every lab_request row starts in.
export const stageOf = (statusKey) =>
  STAGES.find((s) => s.key === statusKey) || STAGES[0];

// Verb used on the "Mark …" button, derived from the NEXT stage label.
const advanceVerb = (next) => {
  if (!next) return '';
  if (next.key === 'sample_taken') return 'Mark Sample Taken';
  if (next.key === 'processing')   return 'Mark Processing';
  if (next.key === 'done')         return 'Mark Done';
  return `Mark ${next.label}`;
};

/**
 * Small status pill. Pass `dense` for a tighter version that fits inside
 * compact list rows.
 */
export const ProgressChip = ({ status, dense = false, sx = {} }) => {
  const s = stageOf(status);
  return (
    <Chip
      size="small"
      icon={s.icon}
      label={dense ? s.short : s.label}
      sx={{
        height: dense ? 22 : 26,
        fontWeight: 700,
        fontSize: dense ? '0.7rem' : '0.75rem',
        color: s.color,
        bgcolor: `${s.color}1A`, // ~10% alpha
        border: `1px solid ${s.color}55`,
        '& .MuiChip-icon': { color: s.color },
        ...sx,
      }}
    />
  );
};

/**
 * Renders a single "advance" button that moves the request to the next
 * stage on click. Pass `reqNo` for the request identifier (the migration
 * 025 tracking ID — LAB-00000x / SCN-00000x) and `status` for the current
 * stage. `onAdvanced(toStatus)` is called after a successful update so
 * the parent can refresh its view.
 *
 * If `status === 'done'` the button doesn't render at all — nothing to
 * advance to. If `reqNo` is missing (legacy row without a tracking ID)
 * the button renders disabled with a tooltip explaining why.
 */
export const AdvanceProgressButton = ({
  reqNo,
  status,
  onAdvanced,
  size = 'small',
  variant = 'outlined',
  changedBy,
  sx = {},
}) => {
  const [busy, setBusy] = useState(false);
  const s = stageOf(status);
  const next = s.next ? stageOf(s.next) : null;

  if (!next) return null; // already done

  const handleClick = async () => {
    if (!reqNo) return;
    setBusy(true);
    try {
      const { data } = await axios.post(advanceRequestProgressAPI, {
        reqNo,
        toStatus: next.key,
        changedBy: changedBy || undefined,
      });
      if (!data?.success) throw new Error(data?.message || 'Could not advance.');
      if (typeof onAdvanced === 'function') onAdvanced(next.key, data);
    } catch (err) {
      await Swal.fire({
        icon: 'error',
        title: 'Could not advance',
        text: err?.response?.data?.message || err.message || 'Unknown error.',
      });
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <Button
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={busy || !reqNo}
      startIcon={busy ? <CircularProgress size={14} /> : next.icon}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 1.5,
        color: next.color,
        borderColor: `${next.color}99`,
        '&:hover': {
          borderColor: next.color,
          bgcolor: `${next.color}14`,
        },
        '&.Mui-disabled': { opacity: 0.55 },
        ...sx,
      }}
    >
      {busy ? 'Updating…' : advanceVerb(next)}
    </Button>
  );

  if (!reqNo) {
    return (
      <Tooltip title="This request was created before progress tracking — no tracking ID is on file.">
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

/**
 * Convenience composite: chip + advance button side by side.
 */
export const ProgressBlock = ({ reqNo, status, onAdvanced, changedBy, sx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
    <ProgressChip status={status} />
    <AdvanceProgressButton
      reqNo={reqNo}
      status={status}
      onAdvanced={onAdvanced}
      changedBy={changedBy}
    />
  </Box>
);

export default ProgressChip;
